// ─────────────────────────────────────────────────────────────────────────────
// MontantInput — champ de saisie MONETAIRE partage
// ─────────────────────────────────────────────────────────────────────────────
// Remplace les <input type="number"> des champs monetaires (27 occurrences /
// 19 ecrans au 23/08/2026). Voir montant.js pour le POURQUOI et montant.test.js
// pour les cas couverts.
//
// TROIS REGLES NON NEGOCIABLES :
//  1. type="text", JAMAIS type="number" — c'est type="number" qui amputait la
//     saisie AVANT que JavaScript ne la voie (bug prouve en prod le 22/08).
//  2. AUCUN reformatage pendant la frappe. Reformater a chaque caractere
//     deplace le curseur et produit des bugs irreproductibles. Saisie BRUTE
//     tant que le champ a le focus, formatage au BLUR uniquement.
//  3. Les decimales suivent la DEVISE (tenants.devise, via useTenant()).
import { useState, useEffect } from 'react';
import { useTenant } from 'context/TenantContext';
import { parseMontant, formatMontant, texteEditable, decimalesDevise } from './montant';

export default function MontantInput({
  value,                 // number | null   (null = non defini, PAS 0)
  onChange,              // (valeur, etat) => void ; etat = { ok, erreur, brut }
  devise,                // optionnel : force la devise (defaut = celle du tenant)
  min = 0,               // borne basse ; null pour desactiver
  max = null,            // borne haute de vraisemblance ; null pour desactiver
  placeholder = '',
  disabled = false,
  required = false,
  className = 'gss-input',
  style,
  afficherDevise = true, // suffixe visuel
  ...rest
}) {
  const tenant = useTenant();
  const dev = devise || tenant?.devise || '';
  const decimales = decimalesDevise(dev);

  const [focus, setFocus] = useState(false);
  const [brut, setBrut] = useState(() => texteEditable(value, dev));
  const [erreur, setErreur] = useState(null);

  // Resynchronisation quand le PARENT change la valeur — mais jamais pendant la
  // frappe : ecraser le texte en cours de saisie est exactement le defaut qu'on
  // veut eviter.
  useEffect(() => {
    if (!focus) {
      setBrut(texteEditable(value, dev));
      setErreur(null);
    }
  }, [value, dev, focus]);

  const valider = (texte) => {
    const r = parseMontant(texte, decimales);
    let err = r.erreur;
    if (r.ok && r.valeur !== null) {
      if (min !== null && min !== undefined && r.valeur < min) err = `Minimum ${min}`;
      else if (max !== null && max !== undefined && r.valeur > max) err = `Maximum ${formatMontant(max, dev)}`;
    }
    return { valeur: err ? null : r.valeur, ok: !err, erreur: err };
  };

  const handleChange = (e) => {
    const texte = e.target.value;
    setBrut(texte);              // <- saisie brute, AUCUN reformatage ici
    const r = valider(texte);
    setErreur(r.erreur);
    // On ne remonte JAMAIS un nombre issu d'une saisie refusee : null + etat.
    // Le parent qui ignore le 2e argument stocke null — jamais une valeur
    // tronquee, qui est precisement le bug d'origine.
    if (onChange) onChange(r.valeur, { ok: r.ok, erreur: r.erreur, brut: texte });
  };

  const handleFocus = () => {
    setFocus(true);
    // En erreur, on conserve ce que la personne a tape : elle doit pouvoir le
    // corriger, pas le retrouver efface.
    if (!erreur) setBrut(texteEditable(value, dev));
  };

  const handleBlur = () => {
    setFocus(false);
    const r = valider(brut);
    if (r.ok) setBrut(texteEditable(r.valeur, dev));
  };

  // Formate seulement au repos ET sans erreur : sinon on montre la saisie reelle.
  const affichage = focus || erreur ? brut : formatMontant(value, dev);

  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', width: style?.width || '100%' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <input
          type="text"                 /* JAMAIS type="number" — cf. en-tete */
          inputMode="decimal"
          autoComplete="off"
          className={className}
          value={affichage}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          required={required}
          placeholder={placeholder}
          aria-invalid={erreur ? 'true' : undefined}
          style={{
            ...style,
            width: '100%',
            borderColor: erreur ? 'var(--gss-danger)' : style?.borderColor,
          }}
          {...rest}
        />
        {afficherDevise && dev && (
          <span style={{ fontSize: 11, color: 'var(--gss-text-secondary)', whiteSpace: 'nowrap' }}>{dev}</span>
        )}
      </span>
      {erreur && (
        <span style={{ fontSize: 11, color: 'var(--gss-danger)', marginTop: 2 }}>{erreur}</span>
      )}
    </span>
  );
}
